import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 5;
const isHeic = (name) => /\.(heic|heif)$/i.test(name);

export function PhotoSection({ photos, setPhotos, labels, testIdPrefix, uploadHint, maxSize, language, t }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const it = language === 'it';

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(t('maxPhotos'));
      return;
    }
    setUploading(true);
    for (const file of files) {
      if (!file.type.startsWith('image/') && !isHeic(file.name)) {
        toast.error(it ? 'Solo immagini sono permesse' : 'Only images are allowed');
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(it ? 'File troppo grande (max 10MB)' : 'File too large (max 10MB)');
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/upload-photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const preview = isHeic(file.name) ? response.data.data : URL.createObjectURL(file);
        setPhotos(prev => [...prev, { filename: response.data.filename, data: response.data.data, preview }]);
      } catch (error) {
        console.error(error);
        toast.error(t('error'));
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <div className="cyber-card rounded-lg p-6 mb-6 cursor-pointer" onClick={() => fileInputRef.current?.click()} data-testid={`${testIdPrefix}-upload-area`}>
        <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif" multiple onChange={handleFileSelect} className="hidden" data-testid={`${testIdPrefix}-input`} />
        <div className="border-2 border-dashed border-neon-green/30 rounded-lg p-6 text-center hover:border-neon-green/60 transition-colors">
          {uploading ? (
            <Loader2 className="w-10 h-10 text-neon-green mx-auto animate-spin" />
          ) : (
            <>
              <Upload className="w-10 h-10 text-neon-green mx-auto mb-3" />
              <p className="font-mono text-white text-sm mb-1">{uploadHint}</p>
              <p className="font-mono text-xs text-gray-500">{maxSize}</p>
            </>
          )}
        </div>
      </div>

      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          {photos.map((photo, index) => (
            <div key={index} className="relative group" data-testid={`${testIdPrefix}-item-${index}`}>
              <img src={photo.preview} alt={photo.filename || `foto-${index + 1}`} className="w-full h-32 object-cover rounded-lg border border-neon-green/30" />
              <button
                onClick={(e) => { e.stopPropagation(); removePhoto(index); }}
                data-testid={`${testIdPrefix}-remove-${index}`}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-neon-red/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              <div className={`absolute bottom-0 left-0 right-0 py-1.5 px-2 rounded-b-lg font-mono text-xs text-white text-center ${
                labels.colorFor(index)
              }`}>
                {labels.nameFor(index)}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="text-center font-mono text-sm text-gray-400 mb-6" data-testid={`${testIdPrefix}-count`}>
        {photos.length}/{MAX_PHOTOS} {labels.countText}
      </div>
    </>
  );
}
